import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Inbox, Briefcase, DollarSign, CheckSquare, TrendingUp, Award, GraduationCap, Building2 } from "lucide-react";

const money = (n) => "$" + (Math.round(n || 0)).toLocaleString();

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/agent/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-slate-400">Loading…</p>;
  const tp = d.training_progress || { completed: 0, total: 1 };
  const pct = tp.total ? Math.round((tp.completed / tp.total) * 100) : 0;
  const activePipeline = Object.entries(d.pipeline || {}).filter(([, v]) => v > 0);

  return (
    <div>
      <PageTitle title="My Dashboard" subtitle="Your placement business at a glance" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard tid="stat-open-opps" label="Open Opportunities" value={d.open_opportunities} icon={Briefcase} />
        <StatCard tid="stat-followups" label="Follow-ups Due" value={d.follow_ups_due} icon={CheckSquare} accent="amber" />
        <StatCard tid="stat-pipeline" label="Active Pipeline" value={d.pipeline_total} icon={TrendingUp} accent="navy" />
        <StatCard tid="stat-potential" label="Potential Commissions" value={money(d.potential_commissions)} icon={DollarSign} accent="emerald" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-navy">Placement pipeline</h3>
          {activePipeline.length === 0 ? (
            <p className="mt-4 text-sm text-slate-400">No active leads yet. Submit your first lead to start your pipeline.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {activePipeline.map(([status, count]) => (
                <div key={status} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5">
                  <span className="text-sm font-medium text-slate-600">{status.replace(/_/g, " ")}</span>
                  <Badge className="bg-clinical text-white hover:bg-clinical">{count}</Badge>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <MiniStat label="Approved" value={money(d.approved_commissions)} accent="text-emeraldbrand" />
            <MiniStat label="Paid" value={money(d.paid_commissions)} accent="text-emeraldbrand" />
            <MiniStat label="Placements" value={d.activity?.placed || 0} accent="text-navy" />
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-6" data-testid="milestone-card">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-clinical" />
              <h3 className="font-heading text-base font-semibold text-navy">Milestone</h3>
            </div>
            <p className="mt-3 font-heading text-2xl font-bold text-navy">{(d.milestone || "").replace(/_/g, " ")}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(d.milestones || []).map((m) => (
                <span key={m} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  m === d.milestone ? "bg-clinical text-white" : "bg-slate-100 text-slate-400"}`}>
                  {m.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-clinical" />
              <h3 className="font-heading text-base font-semibold text-navy">Training progress</h3>
            </div>
            <Progress value={pct} className="mt-4" />
            <p className="mt-2 text-sm text-slate-500">{tp.completed} of {tp.total} modules complete</p>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-clinical" />
              <h3 className="font-heading text-base font-semibold text-navy">Network development</h3>
            </div>
            <p className="mt-3 text-sm text-slate-500">Referral sources: <span className="font-semibold text-navy">{d.referral_development}</span></p>
            <p className="text-sm text-slate-500">Leads submitted: <span className="font-semibold text-navy">{d.activity?.leads}</span></p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-slate-100 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 font-heading text-xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}
